import { isFiniteNumber, isPlainObject, isString, sanitize } from './validate';

const KEY = 'karting-hotlap:stats';

export interface DayStat {
  key: string; // UTC date
  bestMs: number;
  idealMs: number;
  laps: number;
}

export interface Stats {
  days: DayStat[];
  totalLaps: number;
}

const EMPTY: Stats = { days: [], totalLaps: 0 };

/** A saved day shaped the way every consumer below assumes: `recordLap` reads
 *  `d.key`, and the chart sorts on it, so a `null` or half-written row throws
 *  there rather than here. */
function isDayStat(v: unknown): boolean {
  return (
    isPlainObject(v) &&
    isString(v.key) &&
    isFiniteNumber(v.bestMs) &&
    isFiniteNumber(v.idealMs) &&
    isFiniteNumber(v.laps)
  );
}

export function loadStats(): Stats {
  try {
    const raw = localStorage.getItem(KEY);
    // localStorage is user-editable and can outlive the build that wrote it,
    // so e.g. `days` saved as an object rather than an array must not reach
    // the `.map`/`.find` call sites below that assume it is always an array —
    // validate per field on load and fall back to the default silently.
    if (!raw) return { ...EMPTY, days: [] };
    const stats = sanitize(EMPTY, JSON.parse(raw) as Partial<Stats>);
    // Checking the container's type is not enough: the rows are what get
    // dereferenced. Drop only the individually corrupt ones — rejecting the
    // whole array would throw away a player's entire history over one bad row.
    stats.days = stats.days.filter(isDayStat);
    return stats;
  } catch {
    return { ...EMPTY, days: [] };
  }
}

function persist(s: Stats): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* quota or private mode */
  }
}

/** Records a completed valid lap. Keeps one entry per day, the best one. */
export function recordLap(dateKey: string, timeMs: number, idealMs: number): Stats {
  const stats = loadStats();
  stats.totalLaps++;
  const existing = stats.days.find((d) => d.key === dateKey);
  if (existing) {
    existing.laps++;
    if (timeMs < existing.bestMs) existing.bestMs = timeMs;
    existing.idealMs = idealMs;
  } else {
    stats.days.push({ key: dateKey, bestMs: timeMs, idealMs, laps: 1 });
  }
  stats.days.sort((a, b) => a.key.localeCompare(b.key));
  // A year of history is plenty and keeps the payload small.
  if (stats.days.length > 400) stats.days = stats.days.slice(-400);
  persist(stats);
  return stats;
}

function dayNumber(key: string): number {
  return Math.floor(Date.parse(`${key}T00:00:00Z`) / 86400000);
}

/**
 * Consecutive days played ending today or yesterday.
 *
 * Yesterday still counts so the streak does not evaporate before the player has
 * had a chance to play today's track.
 */
export function currentStreak(stats: Stats, todayKey: string): number {
  if (stats.days.length === 0) return 0;
  const played = new Set(stats.days.map((d) => dayNumber(d.key)));
  const today = dayNumber(todayKey);
  const start = played.has(today) ? today : played.has(today - 1) ? today - 1 : null;
  if (start === null) return 0;

  let streak = 0;
  for (let d = start; played.has(d); d--) streak++;
  return streak;
}

export function longestStreak(stats: Stats): number {
  const played = [...new Set(stats.days.map((d) => dayNumber(d.key)))].sort((a, b) => a - b);
  let best = 0;
  let run = 0;
  for (let i = 0; i < played.length; i++) {
    run = i > 0 && played[i] === played[i - 1] + 1 ? run + 1 : 1;
    best = Math.max(best, run);
  }
  return best;
}

/** Gap to ideal as a percentage, the fairest cross-day measure of skill. */
export function gapPercent(d: DayStat): number {
  return ((d.bestMs - d.idealMs) / d.idealMs) * 100;
}

export function bestGapEver(stats: Stats): DayStat | null {
  let best: DayStat | null = null;
  for (const d of stats.days) {
    if (!best || gapPercent(d) < gapPercent(best)) best = d;
  }
  return best;
}
